import { Env } from './env/index.js';
import { isClient } from './env/base.js';
import { urlParse, qsParse, qsStringify } from './url.js';
import { getLocalStorage } from './storage.js';
import { isValidDomain } from './check.js';
export var WebpFeature;
(function (WebpFeature) {
    WebpFeature[WebpFeature["Lossy"] = 1] = "Lossy";
    WebpFeature[WebpFeature["Lossless"] = 2] = "Lossless";
    WebpFeature[WebpFeature["Alpha"] = 3] = "Alpha";
    WebpFeature[WebpFeature["Animation"] = 4] = "Animation"; // 动画
})(WebpFeature = WebpFeature || (WebpFeature = {}));
/**
 * 检查 webp 的支持度
 * @param feature
 * @param callback
 */
export function checkWebpFeature(feature, callback) {
    const kTestImages = {
        [WebpFeature.Lossy]: 'UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA',
        [WebpFeature.Lossless]: 'UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==',
        [WebpFeature.Alpha]: 'UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAARBxAR/Q9ERP8DAABWUDggGAAAABQBAJ0BKgEAAQAAAP4AAA3AAP7mtQAAAA==',
        [WebpFeature.Animation]: 'UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA'
    };
    const img = new Image();
    img.onload = function () {
        const result = img.width > 0 && img.height > 0;
        callback(feature, result);
    };
    img.onerror = function () {
        callback(feature, false);
    };
    img.src = 'data:image/webp;base64,' + kTestImages[feature];
}
const cacheKey = 'webp_supported';
let webpSupported;
/**
 * 更新是否支持 webp，主要用于服务端
 * @param val
 */
export function updateWebpSupported(val) {
    webpSupported = val;
}
/**
 * 是否支持 webp，只要支持无损就算
 */
export function checkWebpSupported() {
    return new Promise((resolve) => {
        if (typeof webpSupported === 'undefined' && isClient) {
            const storage = getLocalStorage();
            const cacheData = storage.getItem(cacheKey, null, 600);
            if (cacheData !== null) {
                if (typeof webpSupported === 'undefined') {
                    updateWebpSupported(cacheData);
                }
                resolve(webpSupported);
            }
            else {
                checkWebpFeature(WebpFeature.Lossless, function (_feature, result) {
                    if (typeof webpSupported === 'undefined') {
                        updateWebpSupported(result);
                    }
                    storage.setItem(cacheKey, result);
                    resolve(result);
                });
            }
        }
        else {
            resolve(!!webpSupported);
        }
    });
}
/**
 * 是否支持 webp
 * 1. 优先使用客户端的检测结果
 * 2. 当客户端的检测还未完成时，使用 ua 判断的结果
 */
export function getWebpSupported(env = null) {
    if (!env) {
        env = Env.getInstance();
    }
    return typeof webpSupported !== 'undefined' ? webpSupported : env.canUseWebp;
}
setTimeout(() => {
    /**
     * 自动检测是否支持 webp
     */
    checkWebpSupported();
}, 100);
/**
 * 图片用到的主域名
 */
const vogionMainDomain = 'voghion.com';
const bucketMap = new Map([
    ['images.voghion.com', 'voghion'],
    ['g-images.voghion.com', 'voghion'],
    ['bbs-g-images.voghion.com', 'bbs-voghion']
]);
const processSubdomain = 'g-images-process';
/**
 * 根据屏幕宽度自适应选择2x、3x、4x的图片，主要用于png图片，png图片使用缩放大概率会变大
 * @param obj
 */
function adaptiveImageSize(obj, env = null) {
    if (!env) {
        env = Env.getInstance();
    }
    let url = '';
    if (env.screenWidth < 390) {
        url = obj['2x'] || '';
    }
    else if (env.screenWidth < 430) {
        url = obj['3x'] || '';
    }
    else {
        url = obj['4x'] || '';
    }
    return url;
}
/**
 * 解析 url 信息，方便后续的参数替换
 * @param url
 * @returns
 */
function parseImageUrl(url, env = null) {
    const urlInfo = urlParse(url);
    const query = qsParse(urlInfo.search);
    let bucket = '';
    let image = '';
    let format = '';
    let resize = '';
    if (urlInfo.host.includes(processSubdomain)) {
        bucket = query.bucket || '';
        image = query.image || '';
        format = query.format || '';
        resize = query.resize || '';
    }
    else {
        if (!env) {
            env = Env.getInstance();
        }
        const mainDomain = env.mainDomain;
        const hostname = urlInfo.hostname;
        for (const domain of bucketMap.keys()) {
            const finalDomain = isValidDomain(mainDomain) ? domain.replace(vogionMainDomain, mainDomain) : domain;
            if (hostname === finalDomain || hostname === domain) {
                bucket = bucketMap.get(domain);
                break;
            }
        }
        if (bucket) {
            image = urlInfo.pathname.slice(1);
        }
    }
    return {
        bucket,
        image,
        format,
        resize
    };
}
/**
 * 获取原图片链接
 * @param {*} url
 */
export function getOriginalImageUrl(url, env = null) {
    if (url.includes(processSubdomain)) {
        const urlInfo = urlParse(url);
        const query = qsParse(urlInfo.search);
        const bucket = query.bucket;
        const image = query.image;
        if (bucket && image) {
            if (!env) {
                env = Env.getInstance();
            }
            const mainDomain = env.mainDomain;
            for (const domain of bucketMap.keys()) {
                const _bucket = bucketMap.get(domain);
                if (_bucket === bucket) {
                    const finalDomain = isValidDomain(mainDomain) ? domain.replace(vogionMainDomain, mainDomain) : domain;
                    return `https://${finalDomain}/${image}`;
                }
            }
        }
    }
    return url;
}
/**
 * 更多图片处理
 * @param url
 */
function convertMore(imageInfo, env = null) {
    const webpSupported = getWebpSupported(env);
    if (webpSupported) {
        imageInfo.format = 'webp';
    }
}
/**
 * 根据图片信息生成缩略图地址
 * @param imageInfo
 */
function buildThumbImageUrl(imageInfo, env = null) {
    if (!imageInfo.bucket) {
        return;
    }
    const query = imageInfo;
    for (const key in query) {
        if (!query[key]) {
            query[key] = undefined;
        }
    }
    if (!env) {
        env = Env.getInstance();
    }
    const mainDomain = env.mainDomain;
    return `https://${processSubdomain}.${isValidDomain(mainDomain) ? mainDomain : vogionMainDomain}/?${qsStringify(query)}`;
}
function formatInputUrl(url, env = null) {
    if (!url) {
        return '';
    }
    if (typeof url !== 'string') {
        url = adaptiveImageSize(url, env);
    }
    return url;
}
/**
 * 限制宽度，主要用在移动端详情页
 * @param {*} url
 * @param {*} width
 */
export function getImageUrlLimitWidth(url, width, env = null) {
    url = formatInputUrl(url, env);
    if (!url) {
        return '';
    }
    const imageInfo = parseImageUrl(url, env);
    if (!imageInfo.bucket) {
        return url;
    }
    if (typeof width === 'string') {
        width = parseInt(width);
    }
    imageInfo.resize = JSON.stringify({
        width
    });
    convertMore(imageInfo, env);
    return buildThumbImageUrl(imageInfo, env);
}
/**
 * 只限制高度
 * @param {*} url
 * @param {*} height
 */
export function getImageUrlLimitHeight(url, height, env = null) {
    url = formatInputUrl(url, env);
    if (!url) {
        return '';
    }
    const imageInfo = parseImageUrl(url, env);
    if (!imageInfo.bucket) {
        return url;
    }
    if (typeof height === 'string') {
        height = parseInt(height);
    }
    imageInfo.resize = JSON.stringify({
        height
    });
    convertMore(imageInfo, env);
    return buildThumbImageUrl(imageInfo, env);
}
/**
 * 限制宽高的缩略图，建议在不需要放大的缩略图场景使用
 * @param {*} url
 * @param {*} width
 * @param {*} height 如不指定，和宽度一直
 */
export function getImageUrlLimitSize(url, width, height, env = null) {
    url = formatInputUrl(url, env);
    if (!url) {
        return '';
    }
    const imageInfo = parseImageUrl(url, env);
    if (!imageInfo.bucket) {
        return url;
    }
    if (typeof width === 'string') {
        width = parseInt(width);
    }
    if (height && typeof height === 'string') {
        height = parseInt(height);
    }
    height = height || width;
    imageInfo.resize = JSON.stringify({
        width,
        height
    });
    convertMore(imageInfo, env);
    return buildThumbImageUrl(imageInfo, env);
}
